import api from './client'

/**
 * Instance-admin billing configuration.
 *
 * Kept separate from the (already large) `instanceAdmin.ts`, same typed style.
 * Every endpoint here is guarded by `require_instance_admin` server-side and
 * every mutation is written to the platform audit log.
 */

const BASE = '/instance-admin/billing'

// ── Sign-up readiness ─────────────────────────────────────────────────────

/** One check of "can a new user sign up, build on welcome credits and carry
 *  on?" — see backend services/billing/readiness.py. `fix.tab` is an Instance
 *  Admin tab, `fix.sub` a Billing sub-tab. */
export interface ReadinessCheck {
  key: string
  label: string
  ok: boolean
  level: 'error' | 'warning'
  detail: string
  fix: { tab: string; sub: string } | null
}

export async function getSignupReadiness(): Promise<{ ready: boolean; checks: ReadinessCheck[] }> {
  const res = await api.get(`${BASE}/readiness`)
  return res.data
}

// ── Plans ─────────────────────────────────────────────────────────────────

export interface AdminPlanPrice {
  id: number
  plan_id: number
  billing_period: string
  currency: string
  country: string | null
  amount_minor: number
  provider: string | null
  provider_plan_id: string | null
  is_active: boolean
}

export interface AdminPlan {
  id: number | null
  code: string
  display_name: string
  description: string | null
  included_monthly_credits: number
  trial_credits: number
  max_applications: number | null
  max_users_per_tenant: number | null
  max_tables_per_project: number | null
  max_screens_per_project: number | null
  max_records_per_project: number | null
  /** Daily turns on the user's own key / subscription / free model; null = unlimited. */
  max_own_ai_turns_per_day: number | null
  /** Daily turns on Twasta AI paid in credits; null = unlimited. */
  max_credit_turns_per_day: number | null
  allow_runtime_download: boolean
  download_fee_usd_minor: number
  download_fee_inr_minor: number
  monthly_input_tokens: number
  monthly_output_tokens: number
  monthly_cost_usd: number
  grace_days: number
  is_active: boolean
  is_public: boolean
  sort_order: number
  prices: AdminPlanPrice[]
  /** Per-plan resource allowances, keyed by the resource-limit catalog key.
   *  Missing key / field = the catalog default. */
  resource_limits?: Record<string, PlanResourceLimit>
}

/** One plan's allowance for one resource: base + per_user × users, capped at max. */
export interface PlanResourceLimit {
  base?: number | null
  per_user?: number | null
  max?: number | null
}

export interface PlanFeature {
  key: string
  label: string
  help: string
}

export async function listPlans(): Promise<{ plans: AdminPlan[]; features: PlanFeature[] }> {
  const res = await api.get(`${BASE}/plans`)
  return res.data
}

export async function createPlan(body: Partial<AdminPlan>): Promise<AdminPlan> {
  const res = await api.post(`${BASE}/plans`, body)
  return res.data
}

export async function updatePlan(planId: number, body: Partial<AdminPlan>): Promise<AdminPlan> {
  const res = await api.put(`${BASE}/plans/${planId}`, body)
  return res.data
}

export async function deactivatePlan(planId: number): Promise<void> {
  await api.delete(`${BASE}/plans/${planId}`)
}

export async function upsertPlanPrice(planId: number, body: {
  billing_period: string
  currency: string
  country?: string | null
  amount_minor: number
  provider?: string | null
  provider_plan_id?: string | null
  is_active?: boolean
}): Promise<AdminPlanPrice[]> {
  const res = await api.put(`${BASE}/plans/${planId}/prices`, body)
  return res.data
}

export async function deletePlanPrice(planId: number, priceId: number): Promise<void> {
  await api.delete(`${BASE}/plans/${planId}/prices/${priceId}`)
}

// ── Credit packs ──────────────────────────────────────────────────────────

export interface AdminPackPrice {
  id: number
  currency: string
  country: string | null
  amount_minor: number
}

export interface AdminPack {
  id: number
  code: string
  name: string
  credits: number
  bonus_credits: number
  visible_plan_codes: string[] | null
  is_active: boolean
  sort_order: number
  prices: AdminPackPrice[]
}

export async function listPacks(): Promise<{ packs: AdminPack[] }> {
  const res = await api.get(`${BASE}/packs`)
  return res.data
}

export async function createPack(body: Partial<AdminPack>): Promise<{ packs: AdminPack[] }> {
  const res = await api.post(`${BASE}/packs`, body)
  return res.data
}

export async function updatePack(packId: number, body: Partial<AdminPack>): Promise<{ packs: AdminPack[] }> {
  const res = await api.put(`${BASE}/packs/${packId}`, body)
  return res.data
}

export async function deletePack(packId: number): Promise<void> {
  await api.delete(`${BASE}/packs/${packId}`)
}

export async function upsertPackPrice(packId: number, body: {
  currency: string; country?: string | null; amount_minor: number
}): Promise<{ packs: AdminPack[] }> {
  const res = await api.put(`${BASE}/packs/${packId}/prices`, body)
  return res.data
}

export async function deletePackPrice(packId: number, priceId: number): Promise<void> {
  await api.delete(`${BASE}/packs/${packId}/prices/${priceId}`)
}

// ── Credit rates & runtime pricing ────────────────────────────────────────

export interface ModelPricingRow {
  id: number
  provider: string | null
  model_prefix: string
  input_usd_per_million: number
  output_usd_per_million: number
  is_active: boolean
}

export interface CreditConversion {
  /** The ONE global ratio: credits burned per USD of provider cost. */
  credits_per_usd: number
  default_credits_per_usd: number
  /** The editable USD-per-token table the dollar value is calculated from. */
  model_pricing: ModelPricingRow[]
  /** Platform seat-price defaults (money minor units). */
  runtime_pricing: Record<string, number>
}

export async function getCreditConversion(): Promise<CreditConversion> {
  const res = await api.get(`${BASE}/credit-conversion`)
  return res.data
}

export async function setCreditsPerUsd(credits_per_usd: number): Promise<CreditConversion> {
  const res = await api.put(`${BASE}/credit-conversion`, { credits_per_usd })
  return res.data
}

export async function upsertModelPricing(body: {
  model_prefix: string; provider?: string | null
  input_usd_per_million: number; output_usd_per_million: number
  is_active?: boolean
}): Promise<CreditConversion> {
  const res = await api.put(`${BASE}/model-pricing`, body)
  return res.data
}

export async function deleteModelPricing(pricingId: number): Promise<CreditConversion> {
  const res = await api.delete(`${BASE}/model-pricing/${pricingId}`)
  return res.data
}

export async function updateRuntimePricing(body: {
  default_runtime_user_cost_usd_minor?: number
  default_runtime_user_cost_inr_minor?: number
  default_pro_invite_cost_usd_minor?: number
  default_pro_invite_cost_inr_minor?: number
}): Promise<Record<string, number>> {
  const res = await api.put(`${BASE}/runtime-pricing`, body)
  return res.data
}

// ── Tax ───────────────────────────────────────────────────────────────────

export interface TaxRule {
  id: number
  country: string | null
  tax_name: string
  rate_percent: number
  components: Array<{ name: string; rate: number }> | null
  applies_to: string
  is_active: boolean
}

export interface InvoiceIdentity {
  legal_name: string
  address: string
  tax_id: string
  seller_state: string
}

export async function listTaxRules(): Promise<{ rules: TaxRule[]; identity: InvoiceIdentity }> {
  const res = await api.get(`${BASE}/tax-rules`)
  return res.data
}

export async function upsertTaxRule(body: {
  country?: string | null; tax_name: string; rate_percent: number
  components?: Array<{ name: string; rate: number }> | null
  applies_to?: string; is_active?: boolean
}): Promise<{ rules: TaxRule[]; identity: InvoiceIdentity }> {
  const res = await api.put(`${BASE}/tax-rules`, body)
  return res.data
}

export async function deleteTaxRule(ruleId: number): Promise<{ rules: TaxRule[]; identity: InvoiceIdentity }> {
  const res = await api.delete(`${BASE}/tax-rules/${ruleId}`)
  return res.data
}

export async function updateInvoiceIdentity(body: Partial<InvoiceIdentity>): Promise<InvoiceIdentity> {
  const res = await api.put(`${BASE}/invoice-identity`, body)
  return res.data
}

// ── Coupons & discounts ───────────────────────────────────────────────────

export interface Coupon {
  id: number
  code: string
  description: string | null
  percent_off: number | null
  fixed_off_minor: number | null
  fixed_off_currency: string | null
  fixed_off_credits: number | null
  scope: string[] | null
  valid_from: string | null
  valid_until: string | null
  max_redemptions: number | null
  per_user_limit: number
  redemptions_count: number
  min_amount_minor: number | null
  is_active: boolean
  created_at: string | null
}

export async function listCoupons(): Promise<{ coupons: Coupon[] }> {
  const res = await api.get(`${BASE}/coupons`)
  return res.data
}

export async function createCoupon(body: Partial<Coupon>): Promise<Coupon> {
  const res = await api.post(`${BASE}/coupons`, body)
  return res.data
}

export async function deactivateCoupon(couponId: number): Promise<Coupon> {
  const res = await api.delete(`${BASE}/coupons/${couponId}`)
  return res.data
}

export async function couponRedemptions(couponId: number) {
  const res = await api.get(`${BASE}/coupons/${couponId}/redemptions`)
  return res.data as {
    redemptions: Array<{ user_id: string; email: string | null; order_id: string | null; redeemed_at: string | null }>
  }
}

export interface Discount {
  id: number
  user_id: string | null
  all_users: boolean
  email: string | null
  user_name: string | null
  percent_off: number | null
  fixed_off_minor: number | null
  fixed_off_credits: number | null
  scope: string[] | null
  applies_period: string | null
  valid_from: string | null
  valid_until: string | null
  max_uses: number | null
  uses_count: number
  is_active: boolean
  note: string | null
}

export async function listDiscounts(): Promise<{ discounts: Discount[] }> {
  const res = await api.get(`${BASE}/discounts`)
  return res.data
}

export async function createDiscount(body: {
  email?: string; user_id?: string; all_users?: boolean
  percent_off?: number; fixed_off_minor?: number; fixed_off_credits?: number
  scope?: string[]; applies_period?: string
  valid_from?: string; valid_until?: string; max_uses?: number; note?: string
}): Promise<{ discounts: Discount[] }> {
  const res = await api.post(`${BASE}/discounts`, body)
  return res.data
}

export async function revokeDiscount(discountId: number): Promise<{ discounts: Discount[] }> {
  const res = await api.delete(`${BASE}/discounts/${discountId}`)
  return res.data
}

// ── Provider ──────────────────────────────────────────────────────────────

export interface ProviderConfig {
  provider: string
  available: Array<{ name: string; enabled: boolean }>
  has_webhook_secret: boolean
  gateway: {
    configured: boolean
    key_id: string
    has_key_secret: boolean
    payments_enabled: boolean
    effective_enabled: boolean
  }
}

export async function getProviderConfig(): Promise<ProviderConfig> {
  const res = await api.get(`${BASE}/payment-provider`)
  return res.data
}

export async function updateProviderConfig(body: {
  provider?: string; webhook_secret?: string
}): Promise<ProviderConfig> {
  const res = await api.put(`${BASE}/payment-provider`, body)
  return res.data
}

// ── Wallets ───────────────────────────────────────────────────────────────

export interface AdminWallet {
  id: number
  owner_type: string
  owner_ref: string
  balance: number
  /** NULL = a credits wallet; a code = MONEY in that currency's minor units. */
  currency: string | null
  display: string | null
  low_balance_threshold: number | null
  label: string
}

export async function listWallets(params?: { owner_type?: string; q?: string }): Promise<{ wallets: AdminWallet[] }> {
  const res = await api.get(`${BASE}/wallets`, { params })
  return res.data
}

export async function adjustWallet(walletId: number, amount: number, description?: string) {
  const res = await api.post(`${BASE}/wallets/${walletId}/adjust`, { amount, description })
  return res.data
}

export async function walletLedger(walletId: number, params?: { limit?: number; offset?: number }) {
  const res = await api.get(`${BASE}/wallets/${walletId}/ledger`, { params })
  return res.data
}

// ── Per-user AI usage ─────────────────────────────────────────────────────
//
// The operator's view is the only one that carries BOTH units: credits (what
// the user was billed) and dollars (what serving them cost the instance).

export interface BillingUser {
  user_id: string
  email: string
  user_name: string | null
  tenant_id: string | null
  is_active: boolean
}

export interface UsageBucket {
  input_tokens: number
  output_tokens: number
  cost_usd: number
  credits: number
  calls: number
}

export interface AdminUserUsage {
  user: BillingUser
  credits_per_usd: number
  wallet: {
    balance: number
    plan_label: string
    scope: string
    credit_billing_enabled: boolean
  } | null
  summary: {
    plan_label: string
    limits: { monthly_input_tokens: number; monthly_output_tokens: number; monthly_cost_usd: number; monthly_credits: number }
    platform: UsageBucket
    own: UsageBucket
    monthly: UsageBucket
    today: UsageBucket
  }
  details: {
    totals: UsageBucket
    daily: Array<{ date: string } & UsageBucket>
    by_task: Array<{ task_type: string } & UsageBucket>
    by_model: Array<{ model: string; provider: string } & UsageBucket>
    by_project: Array<{ project: string } & UsageBucket>
    by_object: Array<{ obj_name: string; task_type: string } & UsageBucket>
    recent: Array<{
      id: number; created_at: string | null; model: string; task_type: string
      obj_name: string | null; input_tokens: number; output_tokens: number
      cost_usd: number; credits: number; duration_ms: number | null; source: string
    }>
  }
  prompts: Array<{
    prompt_id: string; created_at: string | null; project: string | null
    prompt_text: string; model: string | null; status: string
    input_tokens: number; output_tokens: number; cost_usd: number; credits: number
    duration_ms: number | null; calls: number
  }>
}

export async function searchBillingUsers(q?: string): Promise<{ users: BillingUser[] }> {
  const res = await api.get(`${BASE}/users/search`, { params: { q: q || undefined } })
  return res.data
}

export async function userUsage(
  userId: string, params?: { days?: number; date_from?: string; date_to?: string },
): Promise<AdminUserUsage> {
  const res = await api.get(`${BASE}/users/${userId}/usage`, { params })
  return res.data
}

// ── Lifecycle & jobs ──────────────────────────────────────────────────────

export interface LifecycleSettings {
  credit_billing_enabled: boolean
  reminder_days_before: string
  reminder_days: number[]
  grace_reminder_every_days: number
  lapse_policy: 'downgrade' | 'block'
  removal_notice_days: number
  auto_renew_default: boolean
}

export async function getLifecycleSettings(): Promise<LifecycleSettings> {
  const res = await api.get(`${BASE}/lifecycle-settings`)
  return res.data
}

export async function updateLifecycleSettings(body: Partial<LifecycleSettings>): Promise<LifecycleSettings> {
  const res = await api.put(`${BASE}/lifecycle-settings`, body)
  return res.data
}

export interface BillingJob {
  job_name: string
  interval_seconds: number
  last_run_at: string | null
  last_success_at: string | null
  last_status: string | null
  detail: Record<string, unknown>
  is_leader_here: boolean
  runner_enabled: boolean
}

export async function listJobs(): Promise<{ jobs: BillingJob[] }> {
  const res = await api.get(`${BASE}/jobs`)
  return res.data
}

export async function runJob(jobName: string): Promise<{ jobs: BillingJob[]; result: Record<string, unknown> }> {
  const res = await api.post(`${BASE}/jobs/${jobName}/run`)
  return res.data
}

// ── Lapsed users ──────────────────────────────────────────────────────────

export interface LapsedUser {
  user_id: string
  email: string
  user_name: string
  tenant_id: string | null
  plan_code: string
  plan_label: string
  current_period_end: string | null
  lapsed_at: string | null
  days_lapsed: number | null
  last_reminder_at: string | null
  removal_notice_at: string | null
  removal_notice_days: number
  can_remove: boolean
  balance: number
  current_plan_code: string
}

export async function listLapsedUsers(lapsedDays = 0): Promise<{
  users: LapsedUser[]; settings: LifecycleSettings
}> {
  const res = await api.get(`${BASE}/lapsed-users`, { params: { lapsed_days: lapsedDays } })
  return res.data
}

export async function sendLapsedReminder(userId: string) {
  const res = await api.post(`${BASE}/lapsed-users/${encodeURIComponent(userId)}/send-reminder`)
  return res.data
}

export async function sendBulkReminders(userIds: string[]) {
  const res = await api.post(`${BASE}/lapsed-users/send-reminders`, { user_ids: userIds })
  return res.data
}

export async function sendRemovalNotice(userId: string) {
  const res = await api.post(`${BASE}/lapsed-users/${encodeURIComponent(userId)}/send-removal-notice`)
  return res.data
}

export async function removeLapsedUser(userId: string) {
  const res = await api.post(`${BASE}/lapsed-users/${encodeURIComponent(userId)}/remove`)
  return res.data as { user_id: string; downgraded_to: string; projects_deactivated: string[] }
}

export async function userProjects(userId: string) {
  const res = await api.get(`${BASE}/users/${encodeURIComponent(userId)}/projects`)
  return res.data as {
    projects: Array<{ project_id: string; updated_at: string | null; is_active: boolean }>
  }
}

export async function setProjectStatus(
  ownerUserId: string, projectId: string, isActive: boolean, reason?: string,
) {
  const res = await api.put(
    `${BASE}/projects/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(projectId)}/status`,
    { is_active: isActive, reason },
  )
  return res.data
}

export async function listLimitStatus(overOnly = true) {
  const res = await api.get(`${BASE}/limit-status`, { params: { over_only: overOnly } })
  return res.data as {
    projects: Array<{
      owner_user_id: string; project_id: string; plan_code: string | null
      table_count: number | null; screen_count: number | null; record_count: number | null
      max_tables: number | null; max_screens: number | null; max_records: number | null
      any_over: boolean; over_tables: boolean; over_screens: boolean; over_records: boolean
      computed_at: string | null; error: string | null
    }>
  }
}

/** Money in minor units -> a plain major-unit string for an input field. */
export function minorToMajor(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return ''
  return (minor / 100).toFixed(2)
}

/** The inverse, for saving what the admin typed. */
export function majorToMinor(major: string | number): number {
  const value = typeof major === 'number' ? major : parseFloat(major || '0')
  return Math.round((Number.isFinite(value) ? value : 0) * 100)
}

// ── Resource billing: plan cloud-cost rate cards + marketplace ──────────────

export interface AdminMeterRow {
  meter: string
  label: string
  allowance_qty: number | null
  unit_block: number
  rate_usd_minor: number
  rate_inr_minor: number
  over_action: 'charge' | 'block' | 'alert'
  enabled: boolean
  configured: boolean
}

export interface AdminMarketplace {
  resource_billing_enabled: boolean
  resource_billing_observe: boolean
  platform_fee_percent: number
  default_margin_percent: number
  owner_rate_ceiling_multiplier: number
  price_change_notice_days: number
}

export async function getPlanCloudCost(planCode: string): Promise<{
  plan_code: string
  meters: Record<string, AdminMeterRow>
  marketplace: AdminMarketplace
}> {
  const res = await api.get(`/instance-admin/billing/plans/${encodeURIComponent(planCode)}/cloud-cost`)
  return res.data
}

export async function updatePlanCloudCost(
  planCode: string,
  meters: Array<Partial<AdminMeterRow> & { meter: string }>,
): Promise<{ plan_code: string; meters: Record<string, AdminMeterRow> }> {
  const res = await api.put(
    `/instance-admin/billing/plans/${encodeURIComponent(planCode)}/cloud-cost`,
    { meters },
  )
  return res.data
}

export async function getMarketplaceSettings(): Promise<AdminMarketplace> {
  const res = await api.get('/instance-admin/billing/marketplace')
  return res.data
}

export async function updateMarketplaceSettings(
  body: Partial<AdminMarketplace>,
): Promise<AdminMarketplace> {
  const res = await api.put('/instance-admin/billing/marketplace', body)
  return res.data
}
